import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Snackbar } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { registerPushToken } from "../push";

const ChatNotificationsContext = createContext({
  totalUnread: 0,
  unreadByChat: {},
  permission: "unsupported",
  requestPermission: () => {},
});

export const useChatNotifications = () => useContext(ChatNotificationsContext);

const supportsNotifications = () => typeof window !== "undefined" && "Notification" in window;

const BASE_TITLE = document.title;
const PREVIEW_LENGTH = 90;

const preview = (text) =>
  !text ? "" : text.length > PREVIEW_LENGTH ? `${text.slice(0, PREVIEW_LENGTH)}…` : text;

// Escucha en tiempo real los chats del usuario y avisa cuando llega un mensaje nuevo:
// contador de no leídos, aviso dentro de la app y notificación del sistema si la pestaña no está visible.
export const ChatNotificationsProvider = ({ children }) => {
  const [user] = useAuthState(auth);
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadByChat, setUnreadByChat] = useState({});
  const [toast, setToast] = useState(null);
  const [permission, setPermission] = useState(supportsNotifications() ? Notification.permission : "unsupported");

  const pathRef = useRef(location.pathname);
  const lastSeenRef = useRef({});
  const carTitlesRef = useRef({});

  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  const getCarTitle = useCallback(async (carId) => {
    if (!carId) return "";
    if (carTitlesRef.current[carId] === undefined) {
      try {
        const carDoc = await getDoc(doc(db, "cars", carId));
        carTitlesRef.current[carId] = carDoc.data()?.title || "";
      } catch {
        carTitlesRef.current[carId] = "";
      }
    }
    return carTitlesRef.current[carId];
  }, []);

  const openChat = useCallback(
    (chat) => {
      setToast(null);
      navigate(`/chatbox/${chat.id}/${chat.carId}`);
    },
    [navigate]
  );

  const notify = useCallback(
    async (chat) => {
      const carTitle = await getCarTitle(chat.carId);
      const sender = chat.lastSenderName || "Nuevo mensaje";
      const body = preview(chat.lastMessage);

      if (document.visibilityState === "hidden" && supportsNotifications() && Notification.permission === "granted") {
        const notification = new Notification(carTitle ? `${sender} · ${carTitle}` : sender, {
          body,
          icon: `${process.env.PUBLIC_URL}/logo192.png`,
          tag: `chat-${chat.id}`,
        });
        notification.onclick = () => {
          window.focus();
          openChat(chat);
          notification.close();
        };
        return;
      }

      setToast({ chat, sender, carTitle, body });
    },
    [getCarTitle, openChat]
  );

  useEffect(() => {
    setUnreadByChat({});
    lastSeenRef.current = {};
    if (!user) return undefined;

    let initialLoad = true;
    const chatsQuery = query(collection(db, "chats"), where("participants", "array-contains", user.uid));

    const unsubscribe = onSnapshot(
      chatsQuery,
      (snapshot) => {
        const unread = {};
        snapshot.docs.forEach((chatDoc) => {
          const count = chatDoc.data().unreadCount?.[user.uid] || 0;
          if (count > 0) unread[chatDoc.id] = count;
        });
        setUnreadByChat(unread);

        snapshot.docChanges().forEach((change) => {
          const chat = { id: change.doc.id, ...change.doc.data() };
          const time = chat.lastMessageTime?.toMillis?.() || 0;
          const previous = lastSeenRef.current[chat.id];
          lastSeenRef.current[chat.id] = time;

          if (initialLoad || change.type === "removed") return;
          const isNewMessage = time > (previous || 0) && chat.lastSenderId && chat.lastSenderId !== user.uid;
          const isChatOpen = pathRef.current.startsWith(`/chatbox/${chat.id}/`);
          if (isNewMessage && !isChatOpen) notify(chat);
        });
        initialLoad = false;
      },
      () => setUnreadByChat({})
    );

    return () => unsubscribe();
  }, [user, notify]);

  const totalUnread = useMemo(
    () => Object.values(unreadByChat).reduce((sum, count) => sum + count, 0),
    [unreadByChat]
  );

  // Contador en el título de la pestaña
  useEffect(() => {
    document.title = totalUnread > 0 ? `(${totalUnread}) ${BASE_TITLE}` : BASE_TITLE;
  }, [totalUnread]);

  // Con permiso concedido, registrar este dispositivo para recibir push con la app cerrada
  useEffect(() => {
    if (user && permission === "granted") {
      registerPushToken(user.uid).catch(() => {
        // Sin push siguen funcionando los avisos dentro de la app
      });
    }
  }, [user, permission]);

  const requestPermission = useCallback(async () => {
    if (!supportsNotifications()) return;
    const result = await Notification.requestPermission();
    setPermission(result);
  }, []);

  const value = useMemo(
    () => ({ totalUnread, unreadByChat, permission, requestPermission }),
    [totalUnread, unreadByChat, permission, requestPermission]
  );

  return (
    <ChatNotificationsContext.Provider value={value}>
      {children}
      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={(_, reason) => reason !== "clickaway" && setToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ top: { xs: 72, md: 88 } }}
      >
        {toast ? (
          <Alert
            icon={<ChatBubbleOutlineIcon />}
            severity="info"
            variant="filled"
            onClose={() => setToast(null)}
            action={
              <Button color="inherit" size="small" onClick={() => openChat(toast.chat)} sx={{ fontWeight: 700 }}>
                Ver
              </Button>
            }
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              alignItems: "center",
              maxWidth: 380,
              "& .MuiAlert-icon": { color: "secondary.main" },
            }}
          >
            <strong>{toast.sender}</strong>
            {toast.carTitle && ` · ${toast.carTitle}`}
            <br />
            {toast.body}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </ChatNotificationsContext.Provider>
  );
};
